import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import SEO from "./SEO";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "./ui/accordion";

const ServicePageLayout = ({
    testIdPrefix,
    seo,
    jsonLd,
    breadcrumb,
    hero,
    prestations,
    prestationsHeading,
    etapes,
    processHeading,
    pourquoiHeading,
    pourquoiCols,
    faqs,
    faqHeading,
    cta,
}) => {
    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                canonical={seo.canonical}
            />

            {/* JSON-LD : Service */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd.service) }}
            />
            {/* JSON-LD : BreadcrumbList */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd.breadcrumb) }}
            />
            {/* JSON-LD : FAQPage */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd.faq) }}
            />

            {/* HERO */}
            <section
                data-testid={`${testIdPrefix}-hero`}
                className="relative bg-white overflow-hidden"
            >
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, #2C3E45 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
                <div className="container-prose relative py-16 lg:py-24">
                    {/* Breadcrumb */}
                    <nav
                        aria-label="Fil d'Ariane"
                        data-testid={`${testIdPrefix}-breadcrumb`}
                        className="flex items-center gap-2 text-xs text-[#5C6B70] mb-10"
                    >
                        <Link to="/" className="hover:text-[#1C9CAC] transition-colors">
                            Accueil
                        </Link>
                        <ChevronRight size={12} strokeWidth={1.75} />
                        <span>Nos services</span>
                        <ChevronRight size={12} strokeWidth={1.75} />
                        <span className="text-[#2C3E45]">{breadcrumb}</span>
                    </nav>

                    <div className="max-w-3xl animate-fade-up">
                        <div className="inline-flex items-center gap-2 mb-8 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            {hero.eyebrow}
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#2C3E45] leading-[1.05] tracking-tight">
                            {hero.h1Prefix}{" "}
                            <span className="italic text-[#1C9CAC]">
                                {hero.h1Italic}
                            </span>
                        </h1>
                        <p className="mt-6 font-serif italic text-xl sm:text-2xl text-[#5C6B70]">
                            {hero.subtitle}
                        </p>
                        <p className="mt-8 text-lg text-[#5C6B70] max-w-2xl leading-relaxed">
                            {hero.intro}
                        </p>
                    </div>
                </div>
            </section>

            {/* PRESTATIONS */}
            <section
                data-testid={`${testIdPrefix}-prestations`}
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Nos prestations
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            {prestationsHeading}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                        {prestations.map(({ icon: Icon, title, desc }, idx) => (
                            <div
                                key={title}
                                data-testid={`${testIdPrefix}-prestation-card-${idx}`}
                                className="bg-white p-8 lg:p-10 flex flex-col gap-4 group hover:bg-[#FAFAF7] transition-colors"
                            >
                                <div className="w-11 h-11 flex items-center justify-center border border-[#1C9CAC]/30 text-[#1C9CAC] group-hover:bg-[#1C9CAC] group-hover:text-white transition-colors">
                                    <Icon size={20} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-[#2C3E45] mt-2">
                                    {title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMMENT ÇA SE PASSE */}
            <section
                data-testid={`${testIdPrefix}-process`}
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Comment ça se passe
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            {processHeading}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
                        {etapes.map((e, idx) => (
                            <div
                                key={e.num}
                                data-testid={`${testIdPrefix}-etape-${idx}`}
                                className="border-t border-[#1C9CAC] pt-8"
                            >
                                <span className="font-serif text-3xl text-[#1C9CAC] block mb-3">
                                    {e.num}
                                </span>
                                <h3 className="font-serif text-2xl text-[#2C3E45] mb-3 leading-snug">
                                    {e.title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {e.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* POURQUOI FIDUCIAIRE BLEICH */}
            <section
                data-testid={`${testIdPrefix}-pourquoi`}
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-3xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Pourquoi Fiduciaire Bleich
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            {pourquoiHeading}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
                        {pourquoiCols.map((col, idx) => (
                            <div
                                key={idx}
                                data-testid={`${testIdPrefix}-pourquoi-col-${idx}`}
                                className="border-t border-[#1C9CAC] pt-8"
                            >
                                <h3 className="font-serif text-2xl text-[#2C3E45] mb-4 leading-snug">
                                    {col.title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {col.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                data-testid={`${testIdPrefix}-faq`}
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-12">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Questions fréquentes
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            {faqHeading}
                        </h2>
                    </div>

                    <Accordion
                        type="single"
                        collapsible
                        data-testid={`${testIdPrefix}-faq-accordion`}
                        className="max-w-3xl"
                    >
                        {faqs.map((f, idx) => (
                            <AccordionItem
                                key={idx}
                                value={`faq-${idx}`}
                                data-testid={`${testIdPrefix}-faq-item-${idx}`}
                                className="border-b border-[#E5E7EB]"
                            >
                                <AccordionTrigger className="font-serif text-lg sm:text-xl text-[#2C3E45] py-6 hover:no-underline text-left">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[#5C6B70] leading-relaxed text-[15px] pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* PREMIER CONTACT (CTA) */}
            <section
                data-testid={`${testIdPrefix}-cta`}
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Premier contact
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            {cta.heading}
                        </h2>
                        <p className="mt-6 text-lg text-[#5C6B70] leading-relaxed">
                            {cta.text}
                        </p>
                        <div className="mt-10">
                            <Link
                                to="/contact"
                                data-testid={`${testIdPrefix}-cta-rdv`}
                                className="btn-primary"
                            >
                                Prendre rendez-vous
                                <ArrowRight size={16} strokeWidth={1.75} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ServicePageLayout;
